import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useSubscription } from '@apollo/client'
import { useAuth } from '@/components/providers/auth-provider'
import { GET_ACTIVE_CHAT_GENERATIONS, ON_CHAT_GENERATION_STATUS } from '@/lib/graphql/chat'
import { readSavedConversation } from '@/lib/chat-session'
import { toast } from '@/hooks/use-toast'
import { ToastGo } from '@/components/ui/toast-go'

/**
 * Which chats have a reply being written right now, for the whole app.
 *
 * The chat page unmounts when the user opens another page, so it cannot be
 * the one that remembers. This lives above the routes: the menu shows the
 * Create row as working while any reply is being written, the history list
 * marks the conversation, and the chat page reopens it on return.
 *
 * The server is the source of truth: seeded from activeChatGenerations and
 * kept live by onChatGenerationStatus. The chat page also marks a send as
 * started right away, before the server's event arrives.
 */

export interface ChatGenerationEntry {
  conversationId: string
  messageId?: string
  startedAt: number
}

interface ChatActivityValue {
  generating: Map<string, ChatGenerationEntry>
  anyGenerating: boolean
  isGenerating: (conversationId: string | null | undefined) => boolean
  markStarted: (conversationId: string, messageId?: string, startedAt?: number) => void
  markFinished: (conversationId: string, messageId?: string) => void
}

const ChatActivityContext = createContext<ChatActivityValue | null>(null)

/** An entry the server never confirmed or closed is dropped after this. */
const STALE_MS = 15 * 60 * 1000

export function ChatActivityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [generating, setGenerating] = useState<Map<string, ChatGenerationEntry>>(new Map())

  const { data, refetch } = useQuery(GET_ACTIVE_CHAT_GENERATIONS, {
    skip: !user,
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  })

  // The server's list replaces what we had, except sends made in the last few
  // seconds that the server may not have registered yet.
  const applyServerList = useCallback((list?: Array<{ conversationId: string; messageId: string; startedAt: string }>) => {
    if (!list) return
    setGenerating(prev => {
      const next = new Map<string, ChatGenerationEntry>()
      for (const item of list) {
        next.set(item.conversationId, { conversationId: item.conversationId, messageId: item.messageId, startedAt: new Date(item.startedAt).getTime() })
      }
      const now = Date.now()
      for (const [id, entry] of prev) {
        if (!next.has(id) && now - entry.startedAt < 5000) next.set(id, entry)
      }
      return next
    })
  }, [])

  useEffect(() => {
    applyServerList(data?.activeChatGenerations)
  }, [data, applyServerList])

  // Signed out: nothing is being written for anyone here.
  useEffect(() => {
    if (!user) setGenerating(new Map())
  }, [user])

  useSubscription(ON_CHAT_GENERATION_STATUS, {
    skip: !user,
    onData: ({ data: event }) => {
      const status = event?.data?.onChatGenerationStatus
      if (!status?.conversationId) return
      if (status.status === 'started') {
        setGenerating(prev => new Map(prev).set(status.conversationId, {
          conversationId: status.conversationId,
          messageId: status.messageId,
          startedAt: new Date(status.startedAt).getTime(),
        }))
      } else {
        setGenerating(prev => {
          const entry = prev.get(status.conversationId)
          if (!entry || (entry.messageId && status.messageId && entry.messageId !== status.messageId)) return prev
          const next = new Map(prev)
          next.delete(status.conversationId)
          return next
        })
        // The reply landed somewhere the user is not looking: say so, with
        // the way back. On the chat itself, the reply is the notice.
        const viewing = window.location.pathname.startsWith('/chat') && user?.id && readSavedConversation(user.id) === status.conversationId
        if (!viewing) {
          toast({
            title: 'Chat reply finished',
            description: 'The assistant is done writing.',
            action: <ToastGo label="Open" altText="Open the chat" to={`/chat?c=${status.conversationId}`} />,
          })
        }
      }
    },
  })

  // A dropped socket misses events: ask the server again once it is back.
  useEffect(() => {
    if (!user) return
    // Apply the refetched list itself: when it equals the last one, Apollo
    // hands back the same data and the effect above would not run, leaving a
    // reply that ended while the socket was down marked as working.
    const resync = () => {
      void refetch()
        .then(result => applyServerList(result.data?.activeChatGenerations))
        .catch(() => {})
    }
    window.addEventListener('wsReconnected', resync)
    return () => window.removeEventListener('wsReconnected', resync)
  }, [user, refetch, applyServerList])

  // Nothing waits forever on an event that never came.
  useEffect(() => {
    if (generating.size === 0) return
    const timer = window.setInterval(() => {
      const cutoff = Date.now() - STALE_MS
      setGenerating(prev => {
        const stale = [...prev.values()].filter(entry => entry.startedAt < cutoff)
        if (!stale.length) return prev
        const next = new Map(prev)
        for (const entry of stale) next.delete(entry.conversationId)
        return next
      })
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [generating.size])

  const markStarted = useCallback((conversationId: string, messageId?: string, startedAt?: number) => {
    setGenerating(prev => new Map(prev).set(conversationId, { conversationId, messageId, startedAt: startedAt ?? Date.now() }))
  }, [])

  const markFinished = useCallback((conversationId: string, messageId?: string) => {
    setGenerating(prev => {
      const entry = prev.get(conversationId)
      if (!entry || (messageId && entry.messageId && entry.messageId !== messageId)) return prev
      const next = new Map(prev)
      next.delete(conversationId)
      return next
    })
  }, [])

  const value = useMemo<ChatActivityValue>(() => ({
    generating,
    anyGenerating: generating.size > 0,
    isGenerating: (conversationId) => Boolean(conversationId && generating.has(conversationId)),
    markStarted,
    markFinished,
  }), [generating, markStarted, markFinished])

  return <ChatActivityContext.Provider value={value}>{children}</ChatActivityContext.Provider>
}

const inactive: ChatActivityValue = {
  generating: new Map(),
  anyGenerating: false,
  isGenerating: () => false,
  markStarted: () => {},
  markFinished: () => {},
}

export function useChatActivity(): ChatActivityValue {
  return useContext(ChatActivityContext) ?? inactive
}
